import React from "react";
import { Box, Grid, Typography, TextField, Button, Paper, Container, Divider } from "@mui/material";
import { Email, Phone, LocationOn, SupportAgent } from "@mui/icons-material";
import PublicLayout from "../../layouts/PublicLayout";

export default function ContactUs() {
    return (
        <PublicLayout>
            <Container maxWidth="lg">
                <Box sx={{ minHeight: "100vh", pt: 4, pb: 8 }}>

                    {/* Hero Section */}
                    <Box sx={{ maxWidth: "1200px", mx: "auto", textAlign: "center", py: 4, px: 2 }}>
                        <Typography variant="h3" fontWeight={700}>
                            Get in <span style={{ color: "#f6521a" }}>Touch</span>
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{ mt: 2, color: "text.secondary", maxWidth: "700px", mx: "auto", fontSize: "18px" }}
                        >
                            Have a question about TPOS or need technical assistance?
                            Our team is here to help you manage your outlet more efficiently.
                        </Typography>
                    </Box>

                    {/* Main Content Grid */}
                    <Grid container justifyContent="center" spacing={4} sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

                        {/* Left Section: Office Info & Support */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                                {/* Office Address Card */}
                                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocationOn sx={{ color: "#f6521a" }} /> Registered Office
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
                                        <b>ABNScripts Technologies Pvt. Ltd.</b><br />
                                        SR NO.18, ROYAL 18, FLAT NO.201, 19/1/6,<br />
                                        Pimpri Colony, Pimpri-Chinchwad,<br />
                                        Maharashtra 411018, India
                                    </Typography>
                                </Paper>

                                {/* Direct Contact Card */}
                                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: "#ffffff", border: "1px solid #eee" }}>
                                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SupportAgent sx={{ color: "#f6521a" }} /> Support Channels
                                    </Typography>

                                    <Box sx={{ mt: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Phone sx={{ color: "text.secondary", fontSize: 20 }} />
                                            <Typography variant="body1" fontWeight={500}>+91 7066055251</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Email sx={{ color: "text.secondary", fontSize: 20 }} />
                                            <Typography variant="body1" fontWeight={500}>help@truemenu.in</Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 3 }} />

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                                        <b>Grievance Redressal:</b><br />
                                        For regulatory or payment-related escalations, please email our compliance team at help@truemenu.in with your Merchant ID.
                                    </Typography>
                                </Paper>
                            </Box>
                        </Grid>

                        {/* Right Section: Contact Form */}
                        <Grid item xs={12} md={7}>
                            <Paper elevation={3} sx={{ p: 5, borderRadius: 4, bgcolor: "#fff" }}>
                                <Typography variant="h5" fontWeight={800} gutterBottom sx={{ mb: 1 }}>
                                    Send us a message
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                    Our team typically responds within 2 business hours.
                                </Typography>

                                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>

                                    {/* Row 1: Full Name & Business Name */}
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, ml: 0.5 }}>
                                                    Full Name
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    placeholder="e.g. Rahul Sharma"
                                                    variant="outlined"
                                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, ml: 0.5 }}>
                                                    Restaurant / Business Name
                                                </Typography>
                                                <TextField  
                                                    fullWidth
                                                    placeholder="e.g. Truemenu Foods Pvt Ltd"
                                                    variant="outlined"
                                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Row 2: Mobile & Email */}
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, ml: 0.5 }}>
                                                    Mobile Number
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    placeholder="+91 XXXXX XXXXX"
                                                    variant="outlined"
                                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, ml: 0.5 }}>
                                                    Email Address
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    placeholder="name@business.com"
                                                    variant="outlined"
                                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Row 3: Inquiry Type (Full Width) */}
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, ml: 0.5 }}>
                                            Inquiry Type
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            select
                                            SelectProps={{ native: true }}
                                            variant="outlined"
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                        >
                                            <option value="support">Technical Support</option>
                                            <option value="billing">Billing & Subscription</option>
                                            <option value="partnership">Setup Inquiry</option>
                                            <option value="other">General Inquiry</option>
                                        </TextField>
                                    </Box>

                                    {/* Row 4: Message (Expanded) */}
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, ml: 0.5 }}>
                                            Message / Requirement Details
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={5}
                                            placeholder="Please describe your requirement in detail..."
                                            variant="outlined"
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                        />
                                    </Box>

                                    {/* Submit Button */}
                                    <Box sx={{ mt: 1 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            sx={{
                                                px: 8,
                                                py: 2,
                                                fontSize: "16px",
                                                fontWeight: 800,
                                                backgroundColor: "#f6521a",
                                                boxShadow: "0px 8px 16px rgba(246, 82, 26, 0.24)",
                                                ":hover": {
                                                    backgroundColor: "#d94514",
                                                    boxShadow: "0px 12px 20px rgba(246, 82, 26, 0.35)",
                                                },
                                                borderRadius: 3,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Send Message
                                        </Button>
                                    </Box>

                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Map/Trust Section */}
                    <Box sx={{ textAlign: "center", mt: 10, color: "text.secondary", fontSize: "14px" }}>
                        <Typography variant="body2">
                            Serving Merchants across Pune, Mumbai, Bangalore & 50+ Cities in India.
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                            Operating Hours: Mon - Sat | 10:00 AM - 7:00 PM (IST)
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </PublicLayout>
    );
}